/**
 * Global open/close context for the header `Create ▾` menu's dialogs.
 *
 * Mirrors `CommandPaletteContext` — any layout component can imperatively open
 * one of the four create dialogs without owning state. The dialogs themselves
 * are mounted once by `GlobalCreateDialogs`, which reads `active` from here.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export type CreateEntityKind = 'project' | 'contact' | 'product' | 'manufacturer'

interface CreateEntityDialogsContextValue {
  active: CreateEntityKind | null
  openProject: () => void
  openContact: () => void
  openProduct: () => void
  openManufacturer: () => void
  close: () => void
}

const defaultValue: CreateEntityDialogsContextValue = {
  active: null,
  openProject: () => {
    /* no-op until provider mounts */
  },
  openContact: () => {
    /* no-op */
  },
  openProduct: () => {
    /* no-op */
  },
  openManufacturer: () => {
    /* no-op */
  },
  close: () => {
    /* no-op */
  },
}

const CreateEntityDialogsContext =
  createContext<CreateEntityDialogsContextValue>(defaultValue)

interface ProviderProps {
  children: React.ReactNode
}

export const CreateEntityDialogsProvider = ({ children }: ProviderProps): JSX.Element => {
  const [active, setActive] = useState<CreateEntityKind | null>(null)

  const openProject = useCallback(() => setActive('project'), [])
  const openContact = useCallback(() => setActive('contact'), [])
  const openProduct = useCallback(() => setActive('product'), [])
  const openManufacturer = useCallback(() => setActive('manufacturer'), [])
  const close = useCallback(() => setActive(null), [])

  const value = useMemo(
    () => ({ active, openProject, openContact, openProduct, openManufacturer, close }),
    [active, openProject, openContact, openProduct, openManufacturer, close],
  )

  return (
    <CreateEntityDialogsContext.Provider value={value}>
      {children}
    </CreateEntityDialogsContext.Provider>
  )
}

export const useCreateEntityDialogs = (): CreateEntityDialogsContextValue =>
  useContext(CreateEntityDialogsContext)
